import type React from 'react';
import { z } from 'zod';

export type ObjectValues<T> = T[keyof T];

export const IFrameCloseMessageMessageType = 'CLOSE_IFRAME';
export const IFrameDialogTitleOverwriteMessageType = 'DIALOG_TITLE_OVERWRITE';
export const IFramePrimeAssetsReload = 'PRIME_ASSETS_TABLE_RELOAD';
const knownDialogTitleKeys = [
    'assets.assets.create-new-dialog.connector.dialog-title-overwrite.empty',
    'assets.assets.create-new-dialog.connector.dialog-title-overwrite.service-care',
    // 'assets.assets.create-new-dialog.connector.dialog-title-overwrite.basic-asset-data',
] as const;
export type KnownDialogTitleKeys = (typeof knownDialogTitleKeys)[number];

export enum DeviceType {
    MAN_WEBFLEET = 'man-webfleet',
    OCU3 = 'ocu3',
    POCKET_DRIVER_APP = 'pocket-driver-app',
    CM4 = 'cm4',
    CM4_DEV = 'cm4.dev',
    TBM3 = 'tbm3',
    NONE = 'none',
}

export const DEVICE_CONFIG: {
    [k in DeviceType]: {
        isVinRequired: boolean;
        isEulaRequired: boolean;
        isMileageRequired: boolean;
        apiUsedToCreateDevice: 'vcp' | 'vcp-dev' | 'mbb' | 'man-webfleet' | 'none';
    };
} = {
    [DeviceType.MAN_WEBFLEET]: {
        isVinRequired: false,
        isEulaRequired: true,
        isMileageRequired: true,
        apiUsedToCreateDevice: 'man-webfleet',
    },
    [DeviceType.OCU3]: {
        isVinRequired: false,
        isEulaRequired: true,
        isMileageRequired: true,
        apiUsedToCreateDevice: 'mbb',
    },
    [DeviceType.POCKET_DRIVER_APP]: {
        isVinRequired: false,
        isEulaRequired: false,
        isMileageRequired: false,
        apiUsedToCreateDevice: 'none',
    },
    [DeviceType.CM4]: {
        isVinRequired: true,
        isEulaRequired: true,
        isMileageRequired: true,
        apiUsedToCreateDevice: 'vcp',
    },
    [DeviceType.CM4_DEV]: {
        isVinRequired: true,
        isEulaRequired: true,
        isMileageRequired: true,
        apiUsedToCreateDevice: 'vcp-dev',
    },
    [DeviceType.TBM3]: {
        isVinRequired: true,
        isEulaRequired: true,
        isMileageRequired: true,
        apiUsedToCreateDevice: 'vcp',
    },
    [DeviceType.NONE]: {
        isVinRequired: false,
        isEulaRequired: false,
        isMileageRequired: false,
        apiUsedToCreateDevice: 'none',
    },
};

export enum AssetType {
    bus = 'bus',
    car = 'car',
    trailer = 'trailer',
    truck = 'truck',
    van = 'van',
}

export const AssetTypeSchema = z.enum(AssetType);
export const DeviceTypeEnum = z.enum(DeviceType);

export const isDeviceTypeAllowedForTenant = (deviceType: DeviceType, tenant: string | null) =>
    deviceType !== DeviceType.CM4_DEV || tenant === 'rio-eu.test';

export const assetTypeFromString = (raw: string) => {
    const parsed = AssetTypeSchema.safeParse(raw);
    if (parsed.success) {
        return parsed.data;
    } else {
        throw new Error(`could not extract asset type from string: '${raw}'`);
    }
};

export enum IdentificationType {
    vin = 'vin',
}

export enum AssetStatus {
    active = 'active',
    archived = 'archived',
}

export interface AssetInput {
    id: string | null;
    account_id: string | null;
    name: string | null;
    identification: string | null;
    identification_type: IdentificationType | null;
    type: AssetType | null;
    status: AssetStatus;
    brand: string | null;
    license_plate: string | null;
    license_plate_country_code: string | null;
    tags: string[];
    mileage?: number;
}

export interface AssetRest {
    id: string;
    account_id: string;
    name: string;
    identification: string | null;
    identification_type: IdentificationType | null;
    type: AssetType;
    status: AssetStatus;
    brand: string | null;
    license_plate: string | null;
    license_plate_country_code: string | null;
    tags: string[];
}

export enum CreateDialogStepName {
    BASIC_ASSET_DATA = 'BASIC_ASSET_DATA',
    APP_LOGIN = 'APP_LOGIN',
    SERVICE_CARE = 'SERVICE_CARE',
    CLOSE = 'CLOSE',
}

export interface CreateDialogStep {
    body: React.ReactNode;
    showDialogBackButton: boolean;
    showNextButtonArrow: boolean;
    headline: React.ReactNode;
    nextButtonText: string | undefined;
    backButtonText: string;
    nextClickHandler?: () => void;
    backClickHandler?: () => void;
}
